import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Sidebar from '@/components/layout/Sidebar'
import TopBar from '@/components/layout/TopBar'
import Dashboard from '@/pages/Dashboard'
import RiskMap from '@/pages/RiskMap'
import DataExplorer from '@/pages/DataExplorer'
import AlertCenter from '@/pages/AlertCenter'
import MLModels from '@/pages/MLModels'
import Retrocast from '@/pages/Retrocast'

// Page transition variants
const pageVariants = {
  initial: { opacity: 0, y: 12 },
  enter: { opacity: 1, y: 0, transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] } },
  exit: { opacity: 0, y: -8, transition: { duration: 0.15 } },
}

function AnimatedPage({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="enter"
      exit="exit"
      className="min-h-full"
    >
      {children}
    </motion.div>
  )
}

export default function App() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const location = useLocation()

  return (
    <div className="flex h-screen bg-terra-dark overflow-hidden">
      {/* Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed((c) => !c)}
      />

      {/* Main content area */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Top bar */}
        <TopBar />

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-terra-dark bg-grid relative scanline-overlay">
          {/* Ambient background orbs */}
          <div className="ambient-orb ambient-orb-1" />
          <div className="ambient-orb ambient-orb-2" />
          <div className="ambient-orb ambient-orb-3" />

          <div className="min-h-full p-6 relative z-10">
            <AnimatePresence mode="wait">
              <Routes location={location} key={location.pathname}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<AnimatedPage><Dashboard /></AnimatedPage>} />
                <Route path="/risk" element={<AnimatedPage><RiskMap /></AnimatedPage>} />
                <Route path="/data" element={<AnimatedPage><DataExplorer /></AnimatedPage>} />
                <Route path="/alerts" element={<AnimatedPage><AlertCenter /></AnimatedPage>} />
                <Route path="/ml" element={<AnimatedPage><MLModels /></AnimatedPage>} />
                <Route path="/retrocast" element={<AnimatedPage><Retrocast /></AnimatedPage>} />
                {/* Catch-all */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </AnimatePresence>
          </div>
        </main>
      </div>
    </div>
  )
}
